import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'framer-motion'
import { Cookie, Settings, Shield, BarChart3, Megaphone, X } from 'lucide-react'
import { useCookieConsent } from '@/contexts/CookieConsentContext'
import { LocalizedLink } from '@/components/LocalizedLink'

// ─── Custom Toggle Switch ────────────────────────────────────────

function Toggle({ checked, onChange, disabled }: { checked: boolean; onChange: (v: boolean) => void; disabled?: boolean }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => !disabled && onChange(!checked)}
      className="relative inline-flex h-6 w-11 flex-shrink-0 rounded-full transition-colors duration-200 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
      style={{ backgroundColor: checked ? '#2471ac' : '#cbd5e1', cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.6 : 1 }}
    >
      <span
        className="inline-block h-5 w-5 rounded-full bg-white shadow-md transition-transform duration-200"
        style={{ transform: checked ? 'translate(21px, 1px)' : 'translate(1px, 1px)' }}
      />
    </button>
  )
}

// ─── Cookie Preference Row ───────────────────────────────────────

function PreferenceRow({
  icon: Icon,
  title,
  description,
  checked,
  onChange,
  disabled,
  badge,
}: {
  icon: typeof Shield
  title: string
  description: string
  checked: boolean
  onChange: (v: boolean) => void
  disabled?: boolean
  badge?: string
}) {
  return (
    <div className="flex items-center gap-4 rounded-xl bg-surface-light p-4 transition-colors hover:bg-surface-muted">
      <div
        className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg"
        style={{ backgroundColor: checked ? 'rgba(36, 113, 172, 0.1)' : 'rgba(100, 116, 139, 0.1)' }}
      >
        <Icon className="h-5 w-5" style={{ color: checked ? '#2471ac' : '#94a3b8' }} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2">
          <p className="text-sm font-semibold text-text-heading">{title}</p>
          {badge && (
            <span className="inline-flex items-center rounded-full px-2 py-px text-[10px] font-semibold leading-normal text-primary" style={{ backgroundColor: 'rgba(36, 113, 172, 0.1)' }}>
              {badge}
            </span>
          )}
        </div>
        <p className="mt-0.5 text-xs text-text-muted leading-relaxed">{description}</p>
      </div>
      <Toggle checked={checked} onChange={onChange} disabled={disabled} />
    </div>
  )
}

// ─── Main Component ──────────────────────────────────────────────

export function CookieConsent() {
  const { t } = useTranslation()
  const { showBanner, acceptAll, rejectAll, savePreferences } = useCookieConsent()
  const [showPrefs, setShowPrefs] = useState(false)
  const [analytics, setAnalytics] = useState(false)
  const [marketing, setMarketing] = useState(false)

  if (!showBanner) return null

  return (
    <>
      {/* Backdrop overlay when preferences open */}
      <AnimatePresence>
        {showPrefs && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[999] bg-black/40 backdrop-blur-sm"
            onClick={() => setShowPrefs(false)}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {!showPrefs ? (
          /* ─── Compact Banner ────────────────────────── */
          <motion.div
            key="banner"
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed bottom-0 left-0 right-0 z-[1000] border-t border-surface-border bg-white/95 backdrop-blur-md sm:bottom-4 sm:left-4 sm:right-auto sm:max-w-md sm:rounded-2xl sm:border sm:shadow-2xl"
            role="dialog"
            aria-label={t('cookie.title')}
          >
            <div className="p-5">
              {/* Header */}
              <div className="flex items-center gap-3 mb-3">
                <div
                  className="flex h-10 w-10 items-center justify-center rounded-xl"
                  style={{ backgroundColor: 'rgba(201, 133, 23, 0.1)' }}
                >
                  <Cookie className="h-5 w-5" style={{ color: '#c98517' }} />
                </div>
                <h3 className="text-base font-bold text-text-heading">{t('cookie.title', 'Cookie')}</h3>
              </div>

              {/* Message */}
              <p className="text-sm text-text-muted leading-relaxed">
                {t('cookie.message')}{' '}
                <LocalizedLink to="privacy" className="font-medium text-primary hover:underline">
                  {t('cookie.learnMore')}
                </LocalizedLink>
              </p>

              {/* Buttons */}
              <div className="mt-4 flex gap-2">
                <button
                  onClick={acceptAll}
                  className="flex-1 rounded-lg px-4 py-2.5 text-sm font-semibold text-white transition-all hover:opacity-90 active:scale-[0.98]"
                  style={{ backgroundColor: '#c98517' }}
                >
                  {t('cookie.acceptAll', 'Accetta tutto')}
                </button>
                <button
                  onClick={rejectAll}
                  className="flex-1 rounded-lg border-2 border-surface-border px-4 py-2.5 text-sm font-semibold text-text-body transition-all hover:border-text-muted hover:bg-surface-light active:scale-[0.98]"
                >
                  {t('cookie.rejectAll', 'Rifiuta')}
                </button>
              </div>

              {/* Customize link */}
              <button
                onClick={() => setShowPrefs(true)}
                className="mt-3 flex w-full items-center justify-center gap-1.5 text-xs text-text-light transition-colors hover:text-text-muted"
              >
                <Settings className="h-3.5 w-3.5" />
                {t('cookie.customize', 'Personalizza')}
              </button>
            </div>
          </motion.div>
        ) : (
          /* ─── Preferences Panel ─────────────────────── */
          <motion.div
            key="prefs"
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed bottom-0 left-0 right-0 z-[1000] max-h-[85vh] overflow-y-auto bg-white sm:bottom-4 sm:left-4 sm:right-auto sm:max-w-md sm:rounded-2xl sm:shadow-2xl"
            role="dialog"
            aria-label={t('cookie.preferencesTitle')}
          >
            <div className="p-5">
              {/* Header */}
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-3">
                  <div
                    className="flex h-10 w-10 items-center justify-center rounded-xl"
                    style={{ backgroundColor: 'rgba(201, 133, 23, 0.1)' }}
                  >
                    <Settings className="h-5 w-5" style={{ color: '#c98517' }} />
                  </div>
                  <h3 className="text-base font-bold text-text-heading">{t('cookie.preferencesTitle', 'Preferenze Cookie')}</h3>
                </div>
                <button
                  onClick={() => setShowPrefs(false)}
                  className="flex h-8 w-8 items-center justify-center rounded-lg text-text-light transition-colors hover:bg-surface-light hover:text-text-heading"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              {/* Preference Rows */}
              <div className="space-y-3">
                <PreferenceRow
                  icon={Shield}
                  title={t('cookie.necessary', 'Necessari')}
                  description={t('cookie.necessaryDesc', 'Cookie essenziali per il funzionamento del sito')}
                  checked={true}
                  onChange={() => {}}
                  disabled
                  badge={t('cookie.alwaysActive', 'Sempre attivi')}
                />
                <PreferenceRow
                  icon={BarChart3}
                  title={t('cookie.analytics', 'Analitici')}
                  description={t('cookie.analyticsDesc', 'Per analizzare il traffico e migliorare il sito')}
                  checked={analytics}
                  onChange={setAnalytics}
                />
                <PreferenceRow
                  icon={Megaphone}
                  title={t('cookie.marketing', 'Marketing')}
                  description={t('cookie.marketingDesc', 'Per pubblicità personalizzata')}
                  checked={marketing}
                  onChange={setMarketing}
                />
              </div>

              {/* Actions */}
              <div className="mt-5 flex gap-2">
                <button
                  onClick={() => savePreferences({ necessary: true, analytics, marketing })}
                  className="flex-1 rounded-lg px-4 py-2.5 text-sm font-semibold text-white transition-all hover:opacity-90 active:scale-[0.98]"
                  style={{ backgroundColor: '#2471ac' }}
                >
                  {t('cookie.savePrefs', 'Salva preferenze')}
                </button>
                <button
                  onClick={acceptAll}
                  className="flex-1 rounded-lg px-4 py-2.5 text-sm font-semibold text-white transition-all hover:opacity-90 active:scale-[0.98]"
                  style={{ backgroundColor: '#c98517' }}
                >
                  {t('cookie.acceptAll', 'Accetta tutto')}
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}
